import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Card,
  Col,
  Descriptions,
  Form,
  Input,
  List,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Statistic,
  Table,
  Tabs,
  Tag,
  Typography,
  message
} from 'antd';
import {
  CheckCircleOutlined,
  CopyOutlined,
  DeleteOutlined,
  EyeOutlined,
  LinkOutlined,
  MailOutlined,
  PlusOutlined,
  ReloadOutlined,
  SafetyCertificateOutlined,
  SettingOutlined,
  UserOutlined
} from '@ant-design/icons';
import {
  createEmailAccount,
  deleteEmailAccount,
  getEmailAdminMail,
  getEmailAdminMails,
  getEmailCode,
  getEmailInbox,
  getEmailList,
  getEmailMail,
  getEmailStats
} from '../services/api';

const { Title, Text, Paragraph } = Typography;

const EMAIL_DOMAINS = ['dafs.top', 'ruohaotech.com'];

const formatTime = (ts) => {
  if (!ts) return '-';
  const d = new Date(typeof ts === 'number' ? ts : Number(ts) || ts);
  if (Number.isNaN(d.getTime())) return String(ts);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
};

const copyText = async (text) => {
  const v = String(text || '');
  if (!v) return false;
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(v);
    } else {
      const ta = document.createElement('textarea');
      ta.value = v;
      ta.style.position = 'fixed';
      ta.style.left = '-9999px';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
    return true;
  } catch (_) {
    return false;
  }
};

/**
 * 域名邮箱中心：仪表盘 / 账号管理 / 邮件管理（对接原邮箱系统，不迁移旧数据）
 */
const EmailCenter = ({ isMobile, initialTab = 'overview', onNavigate }) => {
  const [tab, setTab] = useState(initialTab);

  useEffect(() => {
    if (initialTab) setTab(initialTab);
  }, [initialTab]);

  const switchTab = (key) => {
    setTab(key);
    if (!onNavigate) return;
    if (key === 'overview') onNavigate('email');
    else if (key === 'accounts') onNavigate('email/accounts');
    else if (key === 'mails') onNavigate('email/mails');
  };
  // —— 仪表盘 ——
  const [statsLoading, setStatsLoading] = useState(false);
  const [emailStats, setEmailStats] = useState(null);

  // —— 账号 / 收件箱 ——
  const [loading, setLoading] = useState(false);
  const [mailboxes, setMailboxes] = useState([]);
  const [search, setSearch] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createForm] = Form.useForm();
  const [activeBox, setActiveBox] = useState(null);
  const [inboxLoading, setInboxLoading] = useState(false);
  const [mails, setMails] = useState([]);
  const [codeLoading, setCodeLoading] = useState(false);
  const [latestCode, setLatestCode] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [mailDetail, setMailDetail] = useState(null);

  // —— 邮件管理（后台 15 天归档）——
  const [adminLoading, setAdminLoading] = useState(false);
  const [adminDay, setAdminDay] = useState('');
  const [adminDomain, setAdminDomain] = useState('');
  const [adminDays, setAdminDays] = useState([]);
  const [adminDomains, setAdminDomains] = useState([]);
  const [adminMails, setAdminMails] = useState([]);
  const [adminView, setAdminView] = useState('mails'); // domains | mails

  const loadStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const res = await getEmailStats();
      if (res?.ok === false) throw new Error(res.error || '获取统计失败');
      setEmailStats(res);
      if (!adminDay && res?.todayKey) setAdminDay(res.todayKey);
      if (Array.isArray(res?.days) && res.days.length) setAdminDays(res.days);
    } catch (e) {
      message.error(e?.message || '获取邮箱统计失败');
    } finally {
      setStatsLoading(false);
    }
  }, [adminDay]);

  const loadMailboxes = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getEmailList();
      setMailboxes(Array.isArray(res?.list) ? res.list : []);
    } catch (e) {
      message.error(e?.message || '获取邮箱列表失败');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadAdminMails = useCallback(async (day, domain) => {
    setAdminLoading(true);
    try {
      const params = {};
      if (day) params.day = day;
      if (domain) params.domain = domain;
      const res = await getEmailAdminMails(params);
      if (res?.ok === false) throw new Error(res.error || '获取邮件失败');
      setAdminMails(Array.isArray(res?.mails) ? res.mails : []);
      setAdminDomains(Array.isArray(res?.domains) ? res.domains : []);
      if (Array.isArray(res?.days) && res.days.length) setAdminDays(res.days);
      if (res?.day) setAdminDay(res.day);
      if (res?.todayKey && !day) setAdminDay(res.todayKey);
    } catch (e) {
      setAdminMails([]);
      message.error(e?.message || '获取邮件列表失败');
    } finally {
      setAdminLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStats();
    loadMailboxes();
  }, [loadStats, loadMailboxes]);

  useEffect(() => {
    if (tab === 'mails') {
      loadAdminMails(adminDay || emailStats?.todayKey || '', adminDomain);
    }
  }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps

  const filteredBoxes = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return mailboxes;
    return mailboxes.filter((m) =>
      String(m.addr || '').toLowerCase().includes(q) ||
      String(m.slug || '').toLowerCase().includes(q)
    );
  }, [mailboxes, search]);

  const openInbox = async (box) => {
    if (!box?.slug) {
      message.warning('该邮箱缺少 slug');
      return;
    }
    setTab('accounts');
    if (onNavigate) onNavigate('email/accounts');
    setActiveBox(box);
    setLatestCode(null);
    setInboxLoading(true);
    try {
      const res = await getEmailInbox(box.slug, 40);
      setMails(Array.isArray(res?.list) ? res.list : []);
      if (res?.address && res.address !== box.addr) {
        setActiveBox({ ...box, addr: res.address, publicUrl: res.publicUrl || box.publicUrl });
      }
    } catch (e) {
      setMails([]);
      message.error(e?.message || '加载收件箱失败');
    } finally {
      setInboxLoading(false);
    }
  };

  const handleCreate = async (values) => {
    setCreating(true);
    try {
      const local = String(values.local || '').trim().toLowerCase().replace(/[^a-z0-9._-]/g, '');
      const domain = values.domain || 'dafs.top';
      if (!local) {
        message.error('请输入邮箱前缀');
        return;
      }
      const res = await createEmailAccount(local, domain);
      if (!res?.ok) {
        message.error(res?.error || '创建失败');
        return;
      }
      let publicUrl = res.publicUrl;
      if (domain === 'dafs.top' && res.slug) {
        publicUrl = `https://email.dafs.top/public/${res.slug}/mails`;
      }
      message.success(`已创建 ${res.addr}`);
      setCreateOpen(false);
      createForm.resetFields();
      await Promise.all([loadMailboxes(), loadStats()]);
      if (res.slug) {
        openInbox({
          addr: res.addr,
          slug: res.slug,
          publicUrl,
          createdAt: res.createdAt || Date.now()
        });
      }
    } catch (e) {
      message.error(e?.message || '创建失败');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (box) => {
    try {
      const res = await deleteEmailAccount(box.slug);
      if (res?.ok === false) {
        message.error(res?.error || '删除失败');
        return;
      }
      message.success('已删除');
      if (activeBox?.slug === box.slug) {
        setActiveBox(null);
        setMails([]);
        setLatestCode(null);
      }
      loadMailboxes();
      loadStats();
    } catch (e) {
      message.error(e?.message || '删除失败');
    }
  };

  const handleFetchCode = async () => {
    if (!activeBox?.slug) return;
    setCodeLoading(true);
    try {
      const res = await getEmailCode(activeBox.slug);
      if (res?.ok && res.code) {
        setLatestCode(res);
        const ok = await copyText(res.code);
        message.success(ok ? `验证码 ${res.code} 已复制` : `验证码：${res.code}`);
      } else {
        message.warning(res?.error || '未找到验证码');
      }
    } catch (e) {
      message.error(e?.message || '取码失败');
    } finally {
      setCodeLoading(false);
    }
  };

  const openMailDetail = async (mail) => {
    if (!activeBox?.slug || !mail?.id) return;
    setDetailOpen(true);
    setDetailLoading(true);
    setMailDetail(null);
    try {
      const res = await getEmailMail(activeBox.slug, mail.id);
      setMailDetail(res);
      if (res?.code) setLatestCode({ code: res.code, mail });
    } catch (e) {
      message.error(e?.message || '加载邮件失败');
      setDetailOpen(false);
    } finally {
      setDetailLoading(false);
    }
  };

  const openAdminMailDetail = async (mail) => {
    if (!mail?.address || !mail?.id) return;
    setDetailOpen(true);
    setDetailLoading(true);
    setMailDetail(null);
    try {
      const res = await getEmailAdminMail(mail.address, mail.id);
      setMailDetail(res);
      if (res?.code) setLatestCode({ code: res.code, mail });
    } catch (e) {
      message.error(e?.message || '加载邮件失败');
      setDetailOpen(false);
    } finally {
      setDetailLoading(false);
    }
  };

  const goTodayDomains = () => {
    switchTab('mails');
    setAdminView('domains');
    const day = emailStats?.todayKey || adminDay;
    setAdminDay(day);
    setAdminDomain('');
    loadAdminMails(day, '');
  };

  const goTodayMails = (domain = '') => {
    switchTab('mails');
    setAdminView('mails');
    const day = emailStats?.todayKey || adminDay;
    setAdminDay(day);
    setAdminDomain(domain);
    loadAdminMails(day, domain);
  };

  const accountColumns = [
    {
      title: '邮箱',
      dataIndex: 'addr',
      key: 'addr',
      ellipsis: true
    },
    {
      title: '创建时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 170,
      render: (v) => formatTime(v)
    },
    {
      title: '操作',
      key: 'action',
      width: isMobile ? 160 : 220,
      render: (_, record) => (
        <Space size="small" wrap>
          <Button type="link" size="small" onClick={() => openInbox(record)}>收件箱</Button>
          <Button
            type="link"
            size="small"
            icon={<LinkOutlined />}
            href={record.publicUrl}
            target="_blank"
            rel="noreferrer"
          >
            打开
          </Button>
          <Popconfirm title="确定删除该邮箱？" onConfirm={() => handleDelete(record)}>
            <Button type="link" size="small" danger icon={<DeleteOutlined />}>删除</Button>
          </Popconfirm>
        </Space>
      )
    }
  ];

  const adminMailColumns = [
    {
      title: '时间',
      dataIndex: 'createdAt',
      width: 170,
      render: (v) => formatTime(v)
    },
    {
      title: '收件地址',
      dataIndex: 'address',
      ellipsis: true
    },
    {
      title: '域名',
      dataIndex: 'domain',
      width: 140,
      render: (v) => <Tag>{v || '-'}</Tag>
    },
    {
      title: '主题',
      dataIndex: 'subject',
      ellipsis: true,
      render: (v) => v || '无主题'
    },
    {
      title: '发件人',
      dataIndex: 'from',
      ellipsis: true,
      width: isMobile ? 120 : 180
    },
    {
      title: '操作',
      key: 'action',
      width: 80,
      render: (_, record) => (
        <Button type="link" size="small" onClick={() => openAdminMailDetail(record)}>查看</Button>
      )
    }
  ];

  const bodyPreview = (() => {
    const parsed = mailDetail?.parsed || {};
    if (parsed.html) return { type: 'html', content: parsed.html };
    if (parsed.text) return { type: 'text', content: parsed.text };
    const snip = mailDetail?.data?.snippet;
    if (snip) return { type: 'text', content: snip };
    return { type: 'text', content: '无正文' };
  })();

  const overviewPanel = (
    <div>
      <div style={{ marginBottom: 16 }}>
        <Title level={4} style={{ margin: 0 }}>仪表盘</Title>
        <Text type="secondary">系统概览和统计信息（对接原域名邮箱后台）</Text>
      </div>
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={12} sm={12} md={6}>
          <Card loading={statsLoading} size="small">
            <Statistic title="已创建邮箱" value={emailStats?.mailboxCount ?? mailboxes.length} prefix={<MailOutlined />} />
          </Card>
        </Col>
        <Col xs={12} sm={12} md={6}>
          <Card
            loading={statsLoading}
            size="small"
            hoverable
            onClick={goTodayDomains}
            style={{ cursor: 'pointer' }}
          >
            <Statistic title="今日收信域名" value={emailStats?.todayDomainCount ?? 0} />
            <Text type="secondary" style={{ fontSize: 12 }}>点击查看域名列表</Text>
          </Card>
        </Col>
        <Col xs={12} sm={12} md={6}>
          <Card
            loading={statsLoading}
            size="small"
            hoverable
            onClick={() => goTodayMails()}
            style={{ cursor: 'pointer' }}
          >
            <Statistic title="今日邮件" value={emailStats?.todayMailCount ?? 0} />
            <Text type="secondary" style={{ fontSize: 12 }}>后台保留 {emailStats?.retainDays || 15} 天</Text>
          </Card>
        </Col>
        <Col xs={12} sm={12} md={6}>
          <Card loading={statsLoading} size="small">
            <Statistic
              title="系统状态"
              value={emailStats?.status === 'ok' || emailStats ? '正常' : '-'}
              valueStyle={{ color: '#3f8600', fontSize: 28 }}
              prefix={<CheckCircleOutlined />}
            />
          </Card>
        </Col>
      </Row>

      <Card title="快速操作" size="small">
        <Space wrap>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => { switchTab('accounts'); setCreateOpen(true); }}>
            添加账号
          </Button>
          <Button icon={<EyeOutlined />} onClick={() => goTodayMails()}>查看邮件</Button>
          <Button icon={<ReloadOutlined />} loading={statsLoading} onClick={() => { loadStats(); loadMailboxes(); }}>
            刷新统计
          </Button>
          <Button icon={<UserOutlined />} onClick={() => switchTab('accounts')}>账号管理</Button>
          <Button icon={<SettingOutlined />} onClick={() => switchTab('mails')}>邮件管理</Button>
        </Space>
      </Card>

      {(emailStats?.todayDomains || []).length > 0 && (
        <Card title="今日收信域名" size="small" style={{ marginTop: 16 }}>
          <Row gutter={[12, 12]}>
            {(emailStats.todayDomains || []).map((d) => (
              <Col xs={12} sm={8} md={6} key={d.domain}>
                <Card
                  size="small"
                  hoverable
                  onClick={() => goTodayMails(d.domain)}
                  style={{ textAlign: 'center' }}
                >
                  <div style={{ fontWeight: 600, marginBottom: 4 }}>{d.domain}</div>
                  <div style={{ fontSize: 22, color: '#1890ff' }}>{d.count}</div>
                  <Text type="secondary" style={{ fontSize: 12 }}>今日邮件</Text>
                </Card>
              </Col>
            ))}
          </Row>
        </Card>
      )}
    </div>
  );

  const accountsPanel = (
    <div>
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={activeBox ? 10 : 24}>
          <Card
            size="small"
            title={`邮箱列表（${filteredBoxes.length}）`}
            extra={
              <Space wrap>
                <Input.Search
                  allowClear
                  placeholder="搜索邮箱"
                  style={{ width: isMobile ? 140 : 200 }}
                  onSearch={setSearch}
                  onChange={(e) => !e.target.value && setSearch('')}
                />
                <Button icon={<ReloadOutlined />} onClick={loadMailboxes} loading={loading}>刷新</Button>
                <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateOpen(true)}>创建邮箱</Button>
              </Space>
            }
          >
            <Table
              rowKey={(r) => r.slug || r.addr}
              loading={loading}
              columns={accountColumns}
              dataSource={filteredBoxes}
              size="small"
              pagination={{ pageSize: 10, showSizeChanger: true }}
              scroll={{ x: 480 }}
              rowClassName={(r) => (activeBox?.slug === r.slug ? 'ant-table-row-selected' : '')}
            />
          </Card>
        </Col>

        {activeBox && (
          <Col xs={24} lg={14}>
            <Card
              size="small"
              title={
                <Space wrap>
                  <span>收件箱</span>
                  <Tag color="blue">{activeBox.addr || activeBox.slug}</Tag>
                </Space>
              }
              extra={
                <Space wrap>
                  <Button
                    type="primary"
                    icon={<SafetyCertificateOutlined />}
                    loading={codeLoading}
                    onClick={handleFetchCode}
                  >
                    一键取码
                  </Button>
                  <Button
                    icon={<ReloadOutlined />}
                    loading={inboxLoading}
                    onClick={() => openInbox(activeBox)}
                  >
                    刷新邮件
                  </Button>
                  <Button
                    icon={<LinkOutlined />}
                    href={activeBox.publicUrl}
                    target="_blank"
                    rel="noreferrer"
                  >
                    外链
                  </Button>
                  <Button onClick={() => { setActiveBox(null); setMails([]); setLatestCode(null); }}>关闭</Button>
                </Space>
              }
            >
              {latestCode?.code && (
                <Card size="small" style={{ marginBottom: 12, background: '#f6ffed', borderColor: '#b7eb8f' }}>
                  <Space wrap>
                    <Text strong>验证码：</Text>
                    <Text style={{ fontSize: 22, letterSpacing: 2, color: '#389e0d' }}>{latestCode.code}</Text>
                    <Button
                      size="small"
                      icon={<CopyOutlined />}
                      onClick={async () => {
                        const ok = await copyText(latestCode.code);
                        message.success(ok ? '已复制' : latestCode.code);
                      }}
                    >
                      复制
                    </Button>
                    {latestCode.mail?.subject && (
                      <Text type="secondary">来自：{latestCode.mail.subject}</Text>
                    )}
                  </Space>
                </Card>
              )}

              <List
                loading={inboxLoading}
                dataSource={mails}
                locale={{ emptyText: '暂无邮件' }}
                renderItem={(item) => (
                  <List.Item
                    actions={[
                      <Button type="link" key="view" onClick={() => openMailDetail(item)}>查看</Button>
                    ]}
                  >
                    <List.Item.Meta
                      title={item.subject || '无主题'}
                      description={
                        <div>
                          <div style={{ fontSize: 12, color: '#8c8c8c' }}>
                            {item.from || '-'} · {formatTime(item.createdAt)}
                          </div>
                          {item.snippet && (
                            <Paragraph ellipsis={{ rows: 2 }} style={{ marginBottom: 0, marginTop: 4, color: '#595959' }}>
                              {item.snippet}
                            </Paragraph>
                          )}
                        </div>
                      }
                    />
                  </List.Item>
                )}
              />
            </Card>
          </Col>
        )}
      </Row>
    </div>
  );

  const mailsPanel = (
    <div>
      <div style={{ marginBottom: 12 }}>
        <Space wrap>
          <Button
            type={adminView === 'domains' ? 'primary' : 'default'}
            onClick={() => setAdminView('domains')}
          >
            今日收信域名
          </Button>
          <Button
            type={adminView === 'mails' ? 'primary' : 'default'}
            onClick={() => setAdminView('mails')}
          >
            邮件列表
          </Button>
          <Select
            style={{ width: 160 }}
            value={adminDay || emailStats?.todayKey}
            options={(adminDays.length ? adminDays : (emailStats?.days || [])).map((d) => ({
              label: d === emailStats?.todayKey ? `${d}（今天）` : d,
              value: d
            }))}
            onChange={(v) => {
              setAdminDay(v);
              setAdminDomain('');
              loadAdminMails(v, '');
            }}
            placeholder="选择日期"
          />
          {adminView === 'mails' && (
            <Select
              allowClear
              style={{ width: 180 }}
              placeholder="按域名筛选"
              value={adminDomain || undefined}
              options={adminDomains.map((d) => ({
                label: `${d.domain}（${d.count}）`,
                value: d.domain
              }))}
              onChange={(v) => {
                const next = v || '';
                setAdminDomain(next);
                loadAdminMails(adminDay || emailStats?.todayKey, next);
              }}
            />
          )}
          <Button
            icon={<ReloadOutlined />}
            loading={adminLoading}
            onClick={() => loadAdminMails(adminDay || emailStats?.todayKey, adminDomain)}
          >
            刷新
          </Button>
        </Space>
        <div style={{ marginTop: 8 }}>
          <Text type="secondary">
            {adminDay || emailStats?.todayKey || '今天'} · 有收信域名 {adminDomains.length} 个
            {adminView === 'mails' ? `，列表 ${adminMails.length} 封` : ''}（后台约保留 15 天）
          </Text>
        </div>
      </div>

      {adminView === 'domains' ? (
        <Row gutter={[12, 12]}>
          {adminDomains.length === 0 && !adminLoading ? (
            <Col span={24}><Text type="secondary">该日暂无收信域名</Text></Col>
          ) : (
            adminDomains.map((d) => (
              <Col xs={12} sm={8} md={6} key={d.domain}>
                <Card
                  size="small"
                  hoverable
                  loading={adminLoading}
                  onClick={() => {
                    setAdminView('mails');
                    setAdminDomain(d.domain);
                    loadAdminMails(adminDay || emailStats?.todayKey, d.domain);
                  }}
                  style={{ textAlign: 'center' }}
                >
                  <div style={{ fontWeight: 600, wordBreak: 'break-all' }}>{d.domain}</div>
                  <div style={{ fontSize: 24, color: '#1890ff', margin: '8px 0 4px' }}>{d.count}</div>
                  <Text type="secondary" style={{ fontSize: 12 }}>当日邮件</Text>
                </Card>
              </Col>
            ))
          )}
        </Row>
      ) : (
        <Table
          rowKey={(r) => `${r.address}-${r.id}`}
          loading={adminLoading}
          columns={adminMailColumns}
          dataSource={adminMails}
          size="small"
          pagination={{ pageSize: 15, showSizeChanger: true }}
          scroll={{ x: 720 }}
        />
      )}
    </div>
  );

  return (
    <div style={{ padding: isMobile ? 12 : 16 }}>
      <div style={{ marginBottom: 12 }}>
        <Title level={4} style={{ margin: 0 }}>
          <MailOutlined style={{ marginRight: 8 }} />
          域名邮箱
        </Title>
        <Text type="secondary">独立对接 Cloudflare 邮箱服务，可看今日收信域名、邮件与账号。</Text>
      </div>

      <Tabs
        activeKey={tab}
        onChange={switchTab}
        items={[
          { key: 'overview', label: '仪表盘', children: overviewPanel },
          { key: 'accounts', label: '账号管理', children: accountsPanel },
          { key: 'mails', label: '邮件管理', children: mailsPanel }
        ]}
      />
      <Modal
        title="创建邮箱"
        open={createOpen}
        onCancel={() => setCreateOpen(false)}
        onOk={() => createForm.submit()}
        confirmLoading={creating}
        destroyOnClose
      >
        <Form
          form={createForm}
          layout="vertical"
          onFinish={handleCreate}
          initialValues={{ domain: 'dafs.top' }}
        >
          <Form.Item
            name="local"
            label="邮箱前缀"
            rules={[{ required: true, message: '请输入前缀' }]}
          >
            <Input placeholder="例如 shop01" />
          </Form.Item>
          <Form.Item name="domain" label="域名" rules={[{ required: true }]}>
            <Select options={EMAIL_DOMAINS.map((d) => ({ label: d, value: d }))} />
          </Form.Item>
          <Text type="secondary">完整地址 = 前缀@域名。创建后数据仍保存在原邮箱系统。</Text>
        </Form>
      </Modal>

      <Modal
        title="邮件详情"
        open={detailOpen}
        onCancel={() => setDetailOpen(false)}
        footer={[
          mailDetail?.code ? (
            <Button
              key="copy"
              type="primary"
              icon={<CopyOutlined />}
              onClick={async () => {
                const ok = await copyText(mailDetail.code);
                message.success(ok ? `已复制 ${mailDetail.code}` : mailDetail.code);
              }}
            >
              复制验证码 {mailDetail.code}
            </Button>
          ) : null,
          <Button key="close" onClick={() => setDetailOpen(false)}>关闭</Button>
        ].filter(Boolean)}
        width={isMobile ? '95%' : 720}
      >
        {detailLoading ? (
          <div style={{ padding: 24, textAlign: 'center' }}>加载中...</div>
        ) : mailDetail ? (
          <div>
            <Descriptions size="small" column={1} bordered style={{ marginBottom: 12 }}>
              <Descriptions.Item label="主题">{mailDetail.data?.subject || '-'}</Descriptions.Item>
              <Descriptions.Item label="发件人">{mailDetail.data?.from || mailDetail.parsed?.from || '-'}</Descriptions.Item>
              <Descriptions.Item label="时间">{formatTime(mailDetail.data?.createdAt)}</Descriptions.Item>
              {mailDetail.code && (
                <Descriptions.Item label="验证码">
                  <Tag color="green">{mailDetail.code}</Tag>
                </Descriptions.Item>
              )}
            </Descriptions>
            <div
              style={{
                border: '1px solid #f0f0f0',
                borderRadius: 8,
                padding: 12,
                maxHeight: 360,
                overflow: 'auto',
                background: '#fafafa'
              }}
            >
              {bodyPreview.type === 'html' ? (
                <div dangerouslySetInnerHTML={{ __html: bodyPreview.content }} />
              ) : (
                <pre style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{bodyPreview.content}</pre>
              )}
            </div>
          </div>
        ) : null}
      </Modal>
    </div>
  );
};

export default EmailCenter;
